import { IPurchase, PurchaseStatus } from '@snackpass/snackpass-types';
import {
    cloneDeep,
    compose,
    groupBy,
    last,
    map,
    prop,
    propOr,
    size,
} from 'lodash/fp';
import { shallowEqual } from 'fast-equals';
import { IOrderStatus, IOrderSource, AppDocument } from '../Types';
import constants from './constants';

export const getPurchaseStatus = (purchase: IPurchase) =>
    compose(prop('type'), last, propOr([], 'status'))(purchase);

// Returns number of seconds left before order is overdue. If the
// order has no pickup time, returns null.
export const getSecondsLeft = (purchase: IPurchase): number | null => {
    if (!purchase.pickupTime) {
        return null;
    }
    const pickUpTime = +new Date(purchase.pickupTime);
    const now = +new Date();
    return (pickUpTime - now) / 1000;
};

export const getMinutesLeft = (purchase: IPurchase): number | null => {
    if (!purchase.pickupTime) {
        return null;
    }
    const pickUpTime = +new Date(purchase.pickupTime);
    const now = +new Date();
    return Math.abs(Math.ceil((pickUpTime - now) / 1000 / 60));
};

export const toTitleCase = (s?: string) => {
    if (!s) return;

    return s
        .split(' ')
        .map(w => w[0].toUpperCase() + w.substring(1).toLowerCase())
        .join(' ');
};

/**
 * Formats the customer's first name, either as a full string or first initial with a period
 * @param name the customer's name string
 * @param onlyShowFirstInitial a boolean of whether this screen is set up to only show first initial or not
 */
export const getFirstName = (
    name: string | null | undefined,
    onlyShowFirstInitial?: boolean
) => {
    let firstName = '';

    if (name) {
        firstName = name.split(' ')[0];
    }

    return onlyShowFirstInitial
        ? `${firstName.charAt(0).toLocaleUpperCase()}.`
        : firstName;
};

const getSectionLengths = compose(
    map(size),
    groupBy((p: IPurchase) => normalizedOrderStatus(p))
);

const getOnePurchasePage = (
    purchases: IPurchase[],
    maxPageSize: number,
    rowSize: number,
    sectionHeaderHeight: number
) => {
    const currentPage = purchases.slice(0, maxPageSize);
    const sectionLengths = getSectionLengths(currentPage);
    let actualPageSize = maxPageSize;

    for (let i = 0; i < sectionLengths.length; i++) {
        // For each section that has a last row that's not full, remove that # of items
        if (sectionLengths[i] % rowSize > 0 || sectionLengths.length > 1) {
            actualPageSize =
                actualPageSize - (rowSize - (sectionLengths[i] % rowSize));
        }
        // For each section remove a row
        if (sectionLengths.length >= 1 && sectionHeaderHeight > 0) {
            actualPageSize -= rowSize;
        }
    }

    const actualPage = purchases.splice(0, actualPageSize);

    return actualPage;
};

let pages: IPurchase[][] = [];
let prevPurchases: IPurchase[] = [];

export const paginatePurchases = (
    purchases: IPurchase[],
    maxPageSize: number,
    rowSize: number,
    sectionHeaderHeight: number
) => {
    if (!shallowEqual(purchases, prevPurchases)) {
        pages = [];
    }

    prevPurchases = cloneDeep(purchases);

    while (purchases.length) {
        pages = [
            ...pages,
            getOnePurchasePage(
                purchases,
                maxPageSize,
                rowSize,
                sectionHeaderHeight
            ),
        ];
    }

    return pages;
};

export const transactionSource = (purchase: IPurchase) => {
    return toTitleCase(purchase.transactionSource) as IOrderSource;
};

export const normalizedOrderStatus = (purchase: IPurchase): IOrderStatus => {
    const purchaseStatus: PurchaseStatus = getPurchaseStatus(purchase);

    if (purchaseStatus) {
        switch (purchaseStatus) {
            case PurchaseStatus.completed: {
                return IOrderStatus.Ready;
            }
            case PurchaseStatus.started:
            case PurchaseStatus.received:
            default: {
                return IOrderStatus.InProgress;
            }
        }
    }
    return IOrderStatus.NotStarted;
};

export function parseStoreName(name?: string) {
    if (!name) {
        return { storeName: '', region: '' };
    }
    const regExp = /\(([^)]+)\)/;
    const matches = regExp.exec(name);
    if (!matches) {
        return { storeName: name, region: '' };
    }
    const matchLength = matches.length;
    const lastPhrase = matches[matchLength - 1];
    return { storeName: name.split('(')[0], region: lastPhrase.split(',')[0] };
}

export function s3ToCloudFrontURL(url: string | null | undefined) {
    if (!url) {
        return '';
    }
    if (url.indexOf('https://menu-pictures.s3.amazonaws.com/') >= 0) {
        const image_path = url.replace(
            'https://menu-pictures.s3.amazonaws.com/',
            ''
        );
        return 'https://d3h3ny262c73zf.cloudfront.net/' + image_path;
    } else {
        return url;
    }
}

export function getDeviceId() {
    return (document as AppDocument).DEVICE_ID?.toLowerCase();
}

export function getDeviceSerial() {
    return (document as AppDocument).SERIAL_NO;
}

export function getDeviceHash() {
    return (
        (document as AppDocument).DEVICE_HASH?.toLowerCase() ??
        (process.env.REACT_APP_ENV === 'local' ? constants.debugSerial : '')
    );
}

export function getDeviceToken() {
    if (process.env.REACT_APP_DEBUG_DEVICE_TOKEN) {
        return process.env.REACT_APP_DEBUG_DEVICE_TOKEN;
    }
    const deviceToken = (document as AppDocument).DEVICE_TOKEN;
    return deviceToken?.length ? deviceToken : undefined;
}

export function getShortId() {
    const snackpassEsperDomain = 'TTY-NYN-';
    const deviceId =
        getDeviceId() ??
        (process.env.REACT_APP_ENV === 'local' ? constants.debugShortId : '');
    const shortId = deviceId.toUpperCase().replace(snackpassEsperDomain, '');

    return shortId;
}

export function getDeviceInfo() {
    const deviceId =
        getDeviceId() ??
        (process.env.REACT_APP_ENV === 'local' ? constants.debugSerial : (process.env.REACT_APP_DEVICE_ID ?? ""));
    const hash = getDeviceHash();

    return { deviceId, hash };
}
